import { notificationService } from '@/services/notificationService'
import { validateEmail } from '@/utils/validation'
import { type ChangeEvent, type FormEvent, useState } from 'react'
import useAccessStore from '../../store/accessStore'

import ForgotPasswordForm from './ForgotPasswordForm'
import styles from './forgotPassword.module.css'

interface ForgotPasswordProps {
  onLinkClick: (page: string) => void
  onSuccess: (email: string) => void
}

interface ForgotPasswordValues {
  email: string
  [key: string]: string
}

interface ForgotPasswordErrors {
  email?: string
  general?: string
  [key: string]: string | undefined
}

export const ForgotPassword = ({ onLinkClick, onSuccess }: ForgotPasswordProps) => {
  const [values, setValues] = useState<ForgotPasswordValues>({
    email: '',
  })
  const [errors, setErrors] = useState<ForgotPasswordErrors>({})
  const [generalError, setGeneralError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { resetPassword } = useAccessStore()

  const validateForm = (): boolean => {
    const newErrors: ForgotPasswordErrors = {}

    const emailError = validateEmail(values.email)
    if (emailError) {
      newErrors.email = emailError
      notificationService.error(emailError)
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target
    setValues((prev) => ({
      ...prev,
      [id]: value,
    }))

    if (errors[id]) {
      setErrors((prev) => ({
        ...prev,
        [id]: '',
      }))
    }

    if (generalError) {
      setGeneralError('')
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setIsLoading(true)
      const resetPasswordResponse = await resetPassword(values.email)
      if (resetPasswordResponse === 200) {
        notificationService.success('Successfully sent code to your email!')
        if (onSuccess) {
          onSuccess(values.email)
        }
      }
    } catch (error: unknown) {
      console.error('Reset password error:', error)
      const responseData = (error as { response?: { data?: Record<string, unknown> } }).response?.data

      if (responseData && typeof responseData.data === 'object' && responseData.data) {
        const backendErrors = responseData.data as Record<string, unknown>
        const newErrors: ForgotPasswordErrors = {}

        for (const [key, value] of Object.entries(backendErrors)) {
          if (typeof value === 'string') {
            newErrors[key] = value
          }
        }

        setErrors(newErrors)
        notificationService.error('Please check your credentials')
      } else {
        const errorMessage = responseData && typeof responseData.message === 'string' ? responseData.message : 'User not found'
        setErrors({
          general: errorMessage,
        })
        notificationService.error(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={styles.mainContentWrapper}>
      <h1 className={styles.title}>Forgot password</h1>
      <p className={styles.textGrey}>Enter your email address and we'll send you a link to reset your password.</p>

      <ForgotPasswordForm
        values={values}
        // errors={errors}
        // generalError={generalError}
        isLoading={isLoading}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        onLinkClick={onLinkClick}
      />

      <div className={styles.additional}>
        <p className={styles.text}>Don't have an account?</p>
        <button
          type="button"
          className={styles.link}
          onClick={() => onLinkClick('registration')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onLinkClick('registration')
            }
          }}
        >
          Sign up
        </button>
      </div>
    </div>
  )
}
